// Dictionary lookup, history, and saved-word storage. The word data is a
// curated export from the Likkutei Sichos dictionary reverse-engineering work.
const { useState: useStateD, useEffect: useEffectD } = React;

const DICTIONARY_KEY = "mayanos-dictionary-v1";
const DICTIONARY_CSV = "/assets/dictionary.csv";

function dictionaryNormalize(value) {
  return String(value || "")
    .normalize("NFKC")
    .replace(/[\u0591-\u05c7]/g, "")
    .replace(/[״׳]/g, "")
    .replace(/[^\p{L}\p{N}'’\- ]/gu, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLocaleLowerCase();
}

function dictionaryDisplay(value) {
  return String(value || "").replace(/[\u0591-\u05c7]/g, "").replace(/\s+/g, " ").trim();
}

function parseDictionaryCsv(text) {
  const rows = [];
  let row = [], field = "", quoted = false;
  const pushField = () => { row.push(field); field = ""; };
  const pushRow = () => { pushField(); if (row.some((value) => value !== "")) rows.push(row); row = []; };
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i += 1; }
      else if (ch === '"') quoted = false;
      else field += ch;
    } else if (ch === '"' && field === "") quoted = true;
    else if (ch === ",") pushField();
    else if (ch === "\n") pushRow();
    else if (ch !== "\r") field += ch;
  }
  if (field || row.length) pushRow();
  if (!rows.length) return [];
  const headers = rows.shift().map((value) => value.trim());
  return rows.map((values) => {
    const item = {};
    headers.forEach((header, index) => { item[header] = values[index] || ""; });
    return item;
  });
}

function dictionaryEntryKey(entry) {
  return dictionaryNormalize(entry && (entry.word || entry.query || entry.original)) || "";
}

function dictionaryTokens(value) {
  return dictionaryNormalize(value).split(" ").filter(Boolean);
}

function compactDictionaryEntry(entry) {
  if (!entry) return null;
  return {
    id: entry.id || dictionaryEntryKey(entry),
    word: dictionaryDisplay(entry.word || entry.query || entry.original),
    translation_en: dictionaryDisplay(entry.translation_en),
    translation_he: dictionaryDisplay(entry.translation_he),
    translation_yi: dictionaryDisplay(entry.translation_yi),
    notes: dictionaryDisplay(entry.notes),
    num_occurrences: Number(entry.num_occurrences || 0),
    source: entry.source || "words_v2",
  };
}

class MayanosDictionaryStore {
  constructor() {
    this.entries = [];
    this.loaded = false;
    this.loading = null;
    this.history = [];
    this.saved = [];
    this.subscribers = new Set();
    this.readLocal();
  }
  readLocal() {
    try {
      const value = JSON.parse(localStorage.getItem(DICTIONARY_KEY) || "{}");
      this.history = Array.isArray(value.history) ? value.history.slice(0, 100) : [];
      this.saved = Array.isArray(value.saved) ? value.saved.slice(0, 100) : [];
    } catch (error) { this.history = []; this.saved = []; }
  }
  writeLocal() {
    try { localStorage.setItem(DICTIONARY_KEY, JSON.stringify(this.snapshot())); } catch (error) {}
  }
  snapshot() { return { history: this.history.slice(0, 100), saved: this.saved.slice(0, 100) }; }
  clearHistory() {
    this.history = [];
    this.writeLocal();
    this.notify();
    if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
  }
  replace(value) {
    if (!value || typeof value !== "object") return;
    const normalizeItem = (item) => ({
      key: dictionaryEntryKey(item) || dictionaryNormalize(item && item.key),
      word: dictionaryDisplay(item && (item.word || item.query || item.original)),
      entry: compactDictionaryEntry(item && (item.entry || item)),
      searchedAt: Number(item && item.searchedAt) || Date.now(),
      found: item && item.found !== false,
    });
    this.history = (Array.isArray(value.history) ? value.history : []).map(normalizeItem).filter((item) => item.key).slice(0, 100);
    this.saved = (Array.isArray(value.saved) ? value.saved : []).map((item) => compactDictionaryEntry(item && (item.entry || item))).filter(Boolean).slice(0, 100);
    this.writeLocal();
    this.notify();
  }
  subscribe(fn) { this.subscribers.add(fn); return () => this.subscribers.delete(fn); }
  notify() { this.subscribers.forEach((fn) => fn(this.snapshot())); }
  async load() {
    if (this.loaded) return this.entries;
    if (this.loading) return this.loading;
    this.loading = fetch(DICTIONARY_CSV, { headers: { Accept: "text/csv" } })
      .then((response) => { if (!response.ok) throw new Error("Dictionary unavailable"); return response.text(); })
      .then((text) => {
        const byKey = new Map();
        parseDictionaryCsv(text).forEach((raw) => {
          if (String(raw.deleted).toLowerCase() === "true") return;
          const entry = compactDictionaryEntry(raw);
          const key = dictionaryEntryKey(entry);
          if (!key || (!entry.translation_en && !entry.translation_he && !entry.translation_yi && !entry.notes)) return;
          const previous = byKey.get(key);
          if (!previous || (entry.translation_en + entry.translation_he + entry.notes).length > (previous.translation_en + previous.translation_he + previous.notes).length) byKey.set(key, entry);
        });
        this.entries = [...byKey.values()];
        this.loaded = true;
        return this.entries;
      })
      .catch((error) => { this.loading = null; throw error; });
    return this.loading;
  }
  async lookup(query) {
    const word = dictionaryDisplay(query);
    const key = dictionaryNormalize(word);
    if (!key) return [];
    await this.load();
    const score = (entry, lookupKey) => {
      const wordKey = dictionaryEntryKey(entry);
      const wordTokens = dictionaryTokens(entry.word);
      const fields = [entry.translation_en, entry.translation_he, entry.translation_yi, entry.notes].map(dictionaryNormalize).filter(Boolean);
      if (wordKey === lookupKey) return 1200;
      if (wordTokens.includes(lookupKey)) return 1100;
      if (wordTokens.some((token) => token.startsWith(lookupKey))) return 1000;
      if (lookupKey.length >= 4 && wordKey.includes(lookupKey)) return 850;
      let best = 0;
      fields.forEach((field) => {
        const tokens = field.split(" ");
        if (field === lookupKey) best = Math.max(best, 780);
        else if (tokens.includes(lookupKey)) best = Math.max(best, 700);
        else if (lookupKey.length >= 3 && field.includes(lookupKey)) best = Math.max(best, 520);
      });
      return best;
    };
    const rank = (lookupKey) => this.entries.map((entry) => ({ entry, score: score(entry, lookupKey) }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score || dictionaryTokens(a.entry.word).length - dictionaryTokens(b.entry.word).length || dictionaryEntryKey(a.entry).localeCompare(dictionaryEntryKey(b.entry)))
      .map((item) => item.entry);
    let candidates = rank(key);
    let lookupMode = candidates.length ? (candidates.some((entry) => dictionaryEntryKey(entry) === key) ? "exact" : "related") : "none";
    const queryTokens = dictionaryTokens(word);
    if (!candidates.length && queryTokens.length > 1) {
      const byEntry = new Map();
      queryTokens.forEach((token) => {
        rank(token).forEach((entry) => {
          const entryKey = dictionaryEntryKey(entry);
          if (entryKey && !byEntry.has(entryKey)) byEntry.set(entryKey, entry);
        });
      });
      candidates = [...byEntry.values()];
      lookupMode = candidates.length ? "split" : "none";
    }
    const results = candidates.slice(0, 8);
    results.lookupMode = lookupMode;
    this.record(word, results[0] || null);
    return results;
  }
  record(word, entry) {
    const key = dictionaryNormalize(word);
    if (!key) return;
    const item = { key, word: dictionaryDisplay(word), entry: compactDictionaryEntry(entry), searchedAt: Date.now(), found: !!entry };
    this.history = [item].concat(this.history.filter((prior) => prior.key !== key)).slice(0, 100);
    this.writeLocal();
    this.notify();
  }
  isSaved(entry) { const key = dictionaryEntryKey(entry); return !!key && this.saved.some((item) => dictionaryEntryKey(item) === key); }
  toggleSaved(entry) {
    const value = compactDictionaryEntry(entry);
    const key = dictionaryEntryKey(value);
    if (!key) return false;
    if (this.isSaved(value)) this.saved = this.saved.filter((item) => dictionaryEntryKey(item) !== key);
    else this.saved = [value].concat(this.saved).slice(0, 100);
    this.writeLocal();
    this.notify();
    if (window.MAYANOS_ACCOUNT_SYNC) window.MAYANOS_ACCOUNT_SYNC.schedule();
    return this.isSaved(value);
  }
}

const MAYANOS_DICTIONARY = new MayanosDictionaryStore();

function DictionaryEntry({ entry, compact = false, onSave }) {
  if (!entry) return null;
  const saved = MAYANOS_DICTIONARY.isSaved(entry);
  return (
    <article className={"dict-entry" + (compact ? " compact" : "")}>
      <div className="dict-entry-head"><b className={entry.word && /[\u0590-\u05ff]/.test(entry.word) ? "heb" : ""}>{entry.word}</b>
        <button type="button" className={"dict-save" + (saved ? " on" : "")} onClick={() => onSave && onSave(entry)} aria-label={saved ? "Remove saved word" : "Save word for later"} title={saved ? "Remove saved word" : "Save for later"}><Icon d={saved ? "check" : "bookmark"} size={14} /></button>
      </div>
      {entry.translation_en && <p className="dict-translation">{entry.translation_en}</p>}
      {entry.translation_he && <p lang="he" dir="rtl" className="dict-secondary heb">{entry.translation_he}</p>}
      {entry.translation_yi && <p className="dict-secondary">{entry.translation_yi}</p>}
      {entry.notes && <p className="dict-notes">{entry.notes}</p>}
      {!compact && entry.num_occurrences > 0 && <small className="dict-occurrences">Appears in {entry.num_occurrences} indexed passages</small>}
    </article>
  );
}

function useDictionaryState() {
  const [state, setState] = useStateD(MAYANOS_DICTIONARY.snapshot());
  useEffectD(() => MAYANOS_DICTIONARY.subscribe(setState), []);
  return state;
}

function DictionaryLookup({ initialQuery = "", compact = false, onOpenPanel, onClose }) {
  const dictionaryState = useDictionaryState();
  const [query, setQuery] = useStateD(initialQuery || "");
  const [results, setResults] = useStateD([]);
  const [loading, setLoading] = useStateD(false);
  const [error, setError] = useStateD("");
  const [mode, setMode] = useStateD(initialQuery ? "lookup" : "history");
  const search = async (value) => {
    const next = dictionaryDisplay(value);
    setQuery(next); setMode("lookup");
    if (!next) { setResults([]); return; }
    setLoading(true); setError("");
    try { setResults(await MAYANOS_DICTIONARY.lookup(next)); }
    catch (reason) { setResults([]); setError("The dictionary is temporarily unavailable."); }
    finally { setLoading(false); }
  };
  useEffectD(() => { if (initialQuery) search(initialQuery); }, [initialQuery]);
  const history = dictionaryState.history || [];
  const saved = dictionaryState.saved || [];
  const normalizedQuery = dictionaryNormalize(query);
  const hasExactEntry = results.some((entry) => dictionaryEntryKey(entry) === normalizedQuery);
  const lookupMode = results.lookupMode || (hasExactEntry ? "exact" : results.length ? "related" : "none");
  return (
    <div className={"dictionary-panel" + (compact ? " dictionary-compact" : "")} role="dialog" aria-label="Mayanos dictionary">
      <div className="dictionary-panel-head"><span><Icon d="book" size={16} /><b>Dictionary</b><i>Look up a word or short phrase</i></span>{onClose && <button type="button" className="dict-close" onClick={onClose} aria-label="Close dictionary"><Icon d="close" size={15} /></button>}</div>
      <form className="dictionary-search" onSubmit={(event) => { event.preventDefault(); search(query); }}>
        <Icon d="search" size={15} /><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); search(event.currentTarget.value); } }} placeholder="Search Hebrew, Yiddish, or English" aria-label="Search dictionary" /><button type="submit" disabled={!query.trim()}>Look up</button>
      </form>
      <div className="dictionary-tabs"><button className={mode === "lookup" ? "on" : ""} onClick={() => setMode("lookup")} type="button">Result</button><button className={mode === "history" ? "on" : ""} onClick={() => setMode("history")} type="button">History <em>{history.length}</em></button><button className={mode === "saved" ? "on" : ""} onClick={() => setMode("saved")} type="button">Saved <em>{saved.length}</em></button></div>
      {mode === "lookup" && <div className="dictionary-results">{loading && <p className="dict-message">Loading the dictionary…</p>}{error && <p className="dict-message">{error}</p>}{!loading && !error && query.trim() && !results.length && <p className="dict-message">No entry yet for “{query}”. Try one or two words exactly as they appear.</p>}{!loading && !error && lookupMode === "related" && <p className="dict-message">No standalone entry for “{query}”; showing related phrases that contain it.</p>}{!loading && !error && lookupMode === "split" && <p className="dict-message">No combined entry for “{query}”; showing the best results for each word.</p>}{results.map((entry) => <DictionaryEntry key={dictionaryEntryKey(entry)} entry={entry} compact={compact} onSave={(value) => MAYANOS_DICTIONARY.toggleSaved(value)} />)}</div>}
      {mode === "history" && <div className="dictionary-list">{history.length ? <>{history.map((item) => <button type="button" className="dict-history-row" key={`${item.key}-${item.searchedAt}`} onClick={() => search(item.word)}><span className={/[\u0590-\u05ff]/.test(item.word) ? "heb" : ""}>{item.word}</span><small>{item.found ? (item.entry && item.entry.translation_en) || "Open result" : "No entry yet"}</small><Icon d="chev" size={13} /></button>)}<button type="button" className="dict-clear-history" onClick={() => MAYANOS_DICTIONARY.clearHistory()}>Clear history</button></> : <p className="dict-message">Words you look up will stay here on this device.</p>}</div>}
      {mode === "saved" && <div className="dictionary-results">{saved.length ? saved.map((entry) => <DictionaryEntry key={dictionaryEntryKey(entry)} entry={entry} compact={compact} onSave={(value) => MAYANOS_DICTIONARY.toggleSaved(value)} />) : <p className="dict-message">Save a word while reading to build your study list.</p>}</div>}
      {!compact && <p className="dict-footnote">Your history and saved words are kept with your Mayanos account when signed in; otherwise they stay on this device.</p>}
      {onOpenPanel && <button type="button" className="dict-open-panel" onClick={onOpenPanel}>Open full dictionary <Icon d="chev" size={13} /></button>}
    </div>
  );
}

function DictionaryPopover({ query, pos, onClose, onOpenPanel }) {
  if (!query || !pos) return null;
  return <div className="dictionary-popover-wrap" style={{ top: pos.top, left: pos.left }}><DictionaryLookup initialQuery={query} compact onClose={onClose} onOpenPanel={onOpenPanel} /></div>;
}

Object.assign(window, { MAYANOS_DICTIONARY, DictionaryEntry, DictionaryLookup, DictionaryPopover, dictionaryNormalize });
